import type { LiveBreakHost, LiveStreamSegment } from "@hobbyshopify/backend-client";
import { breaksLater, currentSegment, formatBreakTime, nextPlanned } from "./lineup";

const drawn = (b: LiveBreakHost) => b.status === "RANDOMIZED" || b.status === "BREAKING";

/**
 * The break whose grid the cockpit shows: the one on screen, else the next box
 * in the line-up, else the first.
 */
export function currentBreak(
  segments: readonly LiveStreamSegment[],
  breaks: readonly LiveBreakHost[],
): LiveBreakHost | null {
  const byId = (id: string | null | undefined) => breaks.find((b) => b.id === id) ?? null;
  return (
    byId(currentSegment(segments)?.breakId) ??
    breaks.find(drawn) ??
    byId(segments.find((s) => s.status === "PLANNED" && s.breakId)?.breakId) ??
    breaks[0] ??
    null
  );
}

export interface StreamTotals {
  sold: number;
  slots: number;
  gross: number;
  cost: number;
}

export function streamTotals(breaks: readonly LiveBreakHost[]): StreamTotals {
  return breaks.reduce(
    (acc, b) => ({
      sold: acc.sold + b.soldCount,
      slots: acc.slots + b.slotCount,
      gross: acc.gross + b.grossSoldCents,
      cost: acc.cost + b.costCents,
    }),
    { sold: 0, slots: 0, gross: 0, cost: 0 },
  );
}

export type CycleMove = "goLive" | "next" | "clear" | "rip" | "finish" | "add";

export interface CycleStep {
  label: string;
  detail: string;
  move: CycleMove;
  /** The break a draw or finish acts on. */
  breakId: string | null;
  /** The segment `next` puts on screen. */
  segmentId: string | null;
  danger: boolean;
  /** Armed on the first press, done on the second: a draw before the box's break time. */
  confirm: "early" | null;
  /** The host confirmed drawing before the break time. */
  force: boolean;
  disabled: boolean;
}

export interface StreamCycle {
  current: LiveStreamSegment | null;
  currentBox: LiveBreakHost | null;
  next: LiveStreamSegment | null;
  /** Segments still to run, the one on screen included. */
  remaining: number;
  step: CycleStep;
}

const base = {
  breakId: null,
  segmentId: null,
  danger: false,
  confirm: null,
  force: false,
  disabled: false,
} as const;

/**
 * The one control that always names the next thing in the stream: put the next
 * segment on screen, draw the box that is on it, close the rip, add more. It
 * never ends the stream; that lives in the menu. Drawing, finishing and adding
 * are the host's; anyone else sees the step greyed out.
 */
export function streamCycle(o: {
  streamStatus: string;
  segments: readonly LiveStreamSegment[];
  breaks: readonly LiveBreakHost[];
  onAir: boolean;
  isHost: boolean;
  now: Date;
}): StreamCycle {
  const { streamStatus, segments, breaks, onAir, isHost, now } = o;
  const live = streamStatus === "LIVE";
  const current = currentSegment(segments);
  const currentBox = breaks.find((b) => b.id === current?.breakId) ?? null;
  const next = nextPlanned(segments);
  const remaining = segments.filter((s) => s.status === "PLANNED").length + (current ? 1 : 0);

  const hostOnly = (step: CycleStep): CycleStep =>
    isHost ? step : { ...step, disabled: true, detail: "Only the stream's host can do this. Take over to do it here." };

  const step = ((): CycleStep => {
    // Closing a rip outranks the camera: a camera that dropped mid-rip must not
    // bury the only way to close the break.
    if (currentBox && drawn(currentBox)) {
      return hostOnly({
        ...base,
        label: "Done ripping",
        detail: next ? `Closes this one out. Next up: ${next.title}.` : "Closes this one out.",
        move: "finish",
        breakId: currentBox.id,
      });
    }

    if (!onAir) {
      return {
        ...base,
        label: live ? "Get back on air" : "Go live",
        detail: live
          ? "Your stream is still on and your viewers are still there."
          : "Turns this device's camera on. Nothing to install.",
        move: "goLive",
      };
    }

    if (currentBox && (currentBox.status === "ON_SALE" || currentBox.status === "LOCKED")) {
      const early = breaksLater(currentBox, now) && currentBox.breakNotBefore !== null;
      return hostOnly({
        ...base,
        label: "Draw and Start the rip",
        detail: early
          ? `Scheduled for ${formatBreakTime(currentBox.breakNotBefore as Date, now)}. Buyers planned for that time.`
          : "Buying stops and a fair draw hands out every spot. It cannot be undone.",
        move: "rip",
        breakId: currentBox.id,
        danger: true,
        confirm: early ? "early" : null,
        force: early,
      });
    }

    if (current) {
      return next
        ? {
            ...base,
            label: `Next: ${next.title}`,
            detail: `Takes ${current.title} off screen.`,
            move: "next",
            segmentId: next.id,
          }
        : {
            ...base,
            label: "Clear the screen",
            detail: "Nothing else is planned. Add to the line-up, or end the stream.",
            move: "clear",
          };
    }

    if (next) {
      return {
        ...base,
        label: `Put ${next.title} on screen`,
        detail: next.kind === "BREAK" ? "Viewers see the box and its spots." : "Viewers see what is on now.",
        move: "next",
        segmentId: next.id,
      };
    }

    return hostOnly({
      ...base,
      label: segments.length > 0 ? "Add more" : "Add a box",
      detail: segments.length > 0 ? "That's the line-up. Add more, or end the stream." : "Add a box to start selling.",
      move: "add",
    });
  })();

  return { current, currentBox, next, remaining, step };
}
